import React, { useState } from 'react'
import {
  ChecklistItemStatus,
  IChecklistItem,
  StatusData,
  errorToString,
  getBadgeClass,
  getChecklistItemDate,
  lenderInspectionTypeForBorrowerInvolvement,
  LoanFundingPhaseShared,
  mdyyyy,
} from '@oneethos/shared'
import { CheckboxIcon } from './checkbox-icon'
import { toast } from 'react-toastify'
import { Spinner } from '@fluentui/react'
import { Modal } from './modal'
import api from '../api-client'

const INSPECTION_ATTESTATIONS = [
  {
    id: 'installationComplete',
    label: 'I confirm that the project has been installed and is ready for a Lender Inspection.',
  },
  {
    id: 'costResponsibility',
    label: 'I understand that if the installation cannot be verified, Installer will be responsible for any costs associated with scheduling another inspection',
  },
  {
    id: 'borrowerInvolvement',
    label: 'I understand and confirm my selection regarding whether borrower involvement is required.',
  },
] as const

type AttestationId = typeof INSPECTION_ATTESTATIONS[number]['id']
type ModalStep = 'requirements' | 'borrower-involvement' | 'attestations'

type ChecklistItemInspectionRequestProps = {
  item: IChecklistItem
  project: StatusData
  onClick: () => void
  isExpanded: boolean
  onProjectChange?: (project: StatusData) => void
}

const ChecklistItemInspectionRequest = ({
  item,
  project,
  onClick,
  isExpanded,
  onProjectChange,
}: ChecklistItemInspectionRequestProps) => {
  const status = LoanFundingPhaseShared.getLoanFundingChecklistItemStatus(item, project)
  const checklistItemDate = getChecklistItemDate(item, project)
  const hasChangeOrderPendingStatus = project.installersStatus === 'Change Order Pending'
  const shouldHideStatusBadge = hasChangeOrderPendingStatus && status !== ChecklistItemStatus.Completed

  const [showModal, setShowModal] = useState(false)
  const [modalStep, setModalStep] = useState<ModalStep>('requirements')
  const [
    requiresBorrowerInvolvement,
    setRequiresBorrowerInvolvement,
  ] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [attestations, setAttestations] = useState<Record<AttestationId, boolean>>({
    installationComplete: false,
    costResponsibility: false,
    borrowerInvolvement: false,
  })

  const allAttested = INSPECTION_ATTESTATIONS.every(itemAttestation => (
    attestations[itemAttestation.id]
  ))

  const handleOpenModal = () => {
    setModalStep('requirements')
    setRequiresBorrowerInvolvement(true)
    setAttestations({
      installationComplete: false,
      costResponsibility: false,
      borrowerInvolvement: false,
    })
    setShowModal(true)
  }

  const handleCloseModal = () => {
    if (submitting) return
    setShowModal(false)
  }

  const toggleAttestation = (id: AttestationId) => {
    setAttestations(prev => ({
      ...prev,
      [id]: !prev[id],
    }))
  }

  const submitInspectionRequest = () => {
    if (!allAttested) {
      toast.error('Please confirm all attestations before requesting an inspection')
      return
    }

    setSubmitting(true)
    api.post(`/loanapps/${project.id}/inspection-request`, {
      lenderInspectionType: lenderInspectionTypeForBorrowerInvolvement(
        requiresBorrowerInvolvement,
      ),
    })
      .then((proj) => {
        const updatedProject = new StatusData(proj)
        onProjectChange?.(updatedProject)
        toast.success('Inspection request submitted')
        setShowModal(false)
      }).catch(err => {
        toast.error(errorToString(err))
      }).finally(() => setSubmitting(false))
  }

  return (
    <>
      <tr onClick={onClick} className="align-middle">
        <td style={{ width: '30px' }}>
          <CheckboxIcon
            isCompleted={status === ChecklistItemStatus.Completed}
            checklistOwner={item.owner}
          />
        </td>
        <td style={{ maxWidth: '300px' }}>
          <div className="fw-bold mb-1 pointer">{item.label}</div>
          <div className={`text-muted small pointer ${isExpanded ? '' : 'd-block text-truncate'}`}>
            {item.description}
          </div>
        </td>
        <td className="text-center">
          {status === ChecklistItemStatus.Completed &&
            <span>{mdyyyy(checklistItemDate)}</span>}
          {!hasChangeOrderPendingStatus && status !== ChecklistItemStatus.Completed &&
            <button
              type="button"
              className="btn btn-tiny btn-primary"
              onClick={(event) => {
                event.stopPropagation()
                handleOpenModal()
              }}
            >
              Request Inspection
            </button>}
          {showModal && (
            <Modal
              onClose={handleCloseModal}
              hideCloseButton
              containerStyles={{ maxWidth: '680px' }}
            >
              {modalStep === 'requirements' && (
                <>
                  <h2 className="fs-3 mb-3">Request Lender Inspection</h2>
                  <p className="fs-5 mb-4">
                    <b>{project.tenantName}</b> requires a <b>Lender Inspection</b> to verify that project
                    equipment has been installed before funds can be released.
                    The installation <b>must be complete</b> before you request the <b>Lender Inspection</b>.
                  </p>
                  <div className="fs-5 mb-4 alert alert-secondary text-start">
                    <div className="fs-6 fw-bold">Per your Installer Agreement, Installation is considered complete when:</div>
                    <div className="mt-2" style={{ fontSize: '0.8rem', fontStyle: 'italic' }}>
                      (a) all material equipment and other improvements included in the financed Project have been
                      installed at the Customer's property in accordance in all respects with the approved project
                      design and applicable permits;(b) the Installer has established and verified connectivity
                      between all applicable system components confirming that installed components respond and
                      communicate as intended and that no known installation or commissioning deficiency must be
                      corrected before the Project is presented for inspection; and (c) Installer has submitted
                      documentation required by Climate First Bank to verify completion of the installation.
                    </div>
                  </div>
                  <div className="alert alert-warning fs-5 mb-5" role="alert">
                    If the installation cannot be verified after submitting this request, funding
                    will be delayed and <b>Installer will be responsible for any fees required to reschedule</b>.
                  </div>
                  <div className="d-flex justify-content-end gap-3">
                    <button
                      type="button"
                      className="btn btn-secondary px-4"
                      onClick={handleCloseModal}
                    >
                      Cancel Request
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={() => setModalStep('borrower-involvement')}
                    >
                      Next
                    </button>
                  </div>
                </>
              )}
              {modalStep === 'borrower-involvement' && (
                <>
                  <h2 className="fs-3 mb-3">Is Borrower Involvement Required?</h2>
                  <div className="alert alert-warning border mb-4" role="region">
                    <div className="fw-bold mb-2">
                      Common Scenarios That Require Borrower Involvement
                    </div>
                    <ul className="mb-0 ps-4 text-start">
                      <li>Batteries are installed in a garage</li>
                      <li>Property exists in a gated community</li>
                      <li>Equipment is not observable without property access</li>
                      <li>Solar panels are only observable from gated back yard</li>
                    </ul>
                  </div>
                  <p className="mb-4">
                    If you are unsure, select <b>Yes, borrower involvement is required</b>. Only select "No, borrower involvement is not required"
                    when all installed equipment can be easily verified without access to the property.
                  </p>
                  <div className="d-flex flex-column gap-3 mb-4">
                    <label className="d-flex align-items-start gap-3 border rounded-3 p-3 pointer">
                      <input
                        type="radio"
                        name="requiresBorrowerInvolvement"
                        className="form-check-input p-2 mt-1 flex-shrink-0"
                        checked={requiresBorrowerInvolvement}
                        onChange={() => setRequiresBorrowerInvolvement(true)}
                      />
                      <span>
                        <span className="fw-bold">Yes, borrower involvement is required</span>
                        <span className="badge text-bg-primary ms-2">Recommended</span>
                      </span>
                    </label>
                    <label className="d-flex align-items-start gap-3 border rounded-3 p-3 pointer">
                      <input
                        type="radio"
                        name="requiresBorrowerInvolvement"
                        className="form-check-input p-2 mt-1 flex-shrink-0"
                        checked={!requiresBorrowerInvolvement}
                        onChange={() => setRequiresBorrowerInvolvement(false)}
                      />
                      <span className="fw-bold">No, borrower involvement is not required</span>
                    </label>
                  </div>
                  <div className="d-flex justify-content-between gap-3">
                    <button
                      type="button"
                      className="btn btn-secondary px-4"
                      onClick={() => setModalStep('requirements')}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={() => setModalStep('attestations')}
                    >
                      Continue
                    </button>
                  </div>
                </>
              )}
              {modalStep === 'attestations' && (
                <>
                  <p className="fs-5 mb-4">
                    Confirm each statement before requesting the Lender Inspection.
                  </p>
                  <div className="d-flex flex-column gap-3 mb-5">
                    {INSPECTION_ATTESTATIONS.map(attestation => (
                      <label
                        key={attestation.id}
                        className="d-flex align-items-start gap-3 border rounded-3 p-3 pointer text-start"
                      >
                        <input
                          type="checkbox"
                          className="form-check-input p-2 mt-1 flex-shrink-0"
                          checked={attestations[attestation.id]}
                          onChange={() => toggleAttestation(attestation.id)}
                          disabled={submitting}
                        />
                        <span className="fs-5">
                          {attestation.id === 'borrowerInvolvement'
                            ? requiresBorrowerInvolvement
                              ? 'I understand that the borrower will need to participate in the Lender Inspection.'
                              : 'I confirm that all installed equipment can be observed from the street without borrower involvement or property access.'
                            : attestation.label}
                        </span>
                      </label>
                    ))}
                  </div>
                  <div className="d-flex justify-content-between gap-3">
                    <button
                      type="button"
                      className="btn btn-secondary px-4"
                      onClick={() => setModalStep('borrower-involvement')}
                      disabled={submitting}
                    >
                      Back
                    </button>
                    <button
                      type="button"
                      className="btn btn-primary px-4"
                      onClick={submitInspectionRequest}
                      disabled={!allAttested || submitting}
                    >
                      {submitting ? <Spinner /> : 'Request Lender Inspection'}
                    </button>
                  </div>
                </>
              )}
            </Modal>
          )}
        </td>
        <td className="text-center">
          {!shouldHideStatusBadge &&
            <span className={`text-capitalize badge rounded-pill ${getBadgeClass(status)}`}>
              {status}
            </span>}
        </td>
        <td className="text-capitalize text-center">{item.owner}</td>
      </tr>
    </>
  )
}

export default ChecklistItemInspectionRequest
